<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Country, CustomerAddress, Salutation } from '~/types/shopware'
import type { Option } from '~/components/atoms/forms/select/Select.vue'
import { buildShopAccountAddressPayload, getInitialBusinessAddressState } from '~/lib/utils/shopAccountAddress'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const { createCustomerAddress, updateCustomerAddress } = useAddress()
const { pushSuccess, pushError } = useNotifications()

const emits = defineEmits<{
  (e: 'success'): void
  (e: 'close'): void
}>()

const props = withDefaults(
  defineProps<{
    address?: CustomerAddress
    countries: Country[]
    salutations: Salutation[]
    salutationLoading?: boolean
    salutationError?: boolean
    onRetrySalutations?: () => void
  }>(),
  {
    address: undefined,
    salutationLoading: false,
    salutationError: false,
    onRetrySalutations: undefined,
  },
)

const { userFromContext } = useSessionContext()

const accountType = userFromContext.value?.accountType
const isBusiness = ref(getInitialBusinessAddressState(props.address, accountType))

const formData = reactive<CustomerAddress>({
  countryId: props.address?.countryId ?? '',
  salutationId: props.address?.salutationId ?? '',
  firstName: props.address?.firstName ?? '',
  lastName: props.address?.lastName ?? '',
  title: props.address?.title ?? '',
  zipcode: props.address?.zipcode ?? '',
  city: props.address?.city ?? '',
  street: props.address?.street ?? '',
  id: props.address?.id ?? '',
  phoneNumber: props.address?.phoneNumber ?? '',
  company: props.address?.company ?? '',
  department: props.address?.department ?? '',
  customerId: props.address?.customerId ?? userFromContext.value?.id ?? '',
})

const invokeSave = async (): Promise<void> => {
  try {
    const saveAddress = formData.id ? updateCustomerAddress : createCustomerAddress
    await saveAddress(buildShopAccountAddressPayload(formData, isBusiness.value))
    pushSuccess(t('shop.addressCard.addressForm.addressSavedSuccessfully'))
    emits('success')
  } catch {
    pushError(t('shop.addressCard.addressForm.savingAddressFailed'))
  }
}

const salutationOptions = computed<Option[]>(() =>
  props.salutations.map((salutation) => ({
    value: salutation.id,
    label: salutation.translated?.displayName || salutation.displayName,
  })),
)
const countryOptions = computed<Option[]>(() =>
  props.countries.map((country) => ({
    value: country.id,
    label: country.name,
  })),
)
const swissCountryId = computed(() => props.countries.find((country) => country.iso === 'CH')?.id)

const { city, handleCityInput, handleZipCodeInput } = await useCities({
  initialCity: formData.city,
  countryId: () => formData.countryId,
  swissCountryId,
})
</script>

<template>
<FormKitLazyProvider config-file="true">

  <FormKit v-model="formData" type="form" :actions="false" :incomplete-message="false" @submit="invokeSave">
    <div class="wrapper">
      <div class="salutation-field">
        <Select
          id="address-form-salutationId"
          name="salutationId"
          :required="true"
          :label="`${t('shop.registrationPage.registrationForm.salutation')} *`"
          :options="salutationOptions"
        />
        <p v-if="salutationLoading && !salutationOptions.length" class="salutation-loading" role="status">
          {{ t('shop.salutationField.loading') }}
        </p>
        <p v-if="salutationError" class="salutation-error" role="alert">
          {{ t('shop.salutationField.loadError') }}
        </p>
        <Button
          v-if="salutationError && onRetrySalutations"
          secondary
          type="button"
          :disabled="salutationLoading"
          @click="onRetrySalutations"
        >
          {{ t('shop.salutationField.retry') }}
        </Button>
      </div>
      <Input
        id="address-form-title"
        name="title"
        :required="false"
        :label="t('shop.registrationPage.registrationForm.title')"
      />
      <Input
        id="address-form-firstName"
        name="firstName"
        :required="true"
        :label="`${t('shop.registrationPage.registrationForm.firstName')} *`"
      />
      <Input
        id="address-form-lastName"
        name="lastName"
        :required="true"
        :label="`${t('shop.registrationPage.registrationForm.lastName')} *`"
      />
      <Checkbox
        id="address-form-businessAddress"
        v-model="isBusiness"
        name="businessAddress"
        :required="false"
        :label="t('shop.accountPage.addressForm.businessAddress')"
        :ignore="true"
        toggle
      />
      <Input
        v-if="isBusiness"
        id="address-form-company"
        name="company"
        :required="true"
        validation="required:trim"
        :label="`${t('shop.registrationPage.registrationForm.company')} *`"
      />
      <Input
        v-if="isBusiness"
        id="address-form-department"
        name="department"
        :required="false"
        :label="t('shop.registrationPage.registrationForm.department')"
      />
      <Input
        id="address-form-street"
        name="street"
        :required="true"
        :label="`${t('shop.registrationPage.registrationForm.street')} *`"
      />
      <Input
        id="address-form-zipcode"
        name="zipcode"
        :required="true"
        :label="`${t('shop.registrationPage.registrationForm.postalCode')} *`"
        validation="number"
        @input.capture="handleZipCodeInput"
      />
      <Input
        id="address-form-city"
        v-model="city"
        name="city"
        :required="true"
        :label="`${t('shop.registrationPage.registrationForm.city')} *`"
        @input.capture="handleCityInput"
      />

      <TelInput
        id="address-form-phoneNumber"
        name="phoneNumber"
        :required="true"
        :label="`${t('shop.registrationPage.registrationForm.phoneNumber')} *`"
      />
      <Select
        id="address-form-countryId"
        name="countryId"
        :required="true"
        :label="`${t('shop.registrationPage.registrationForm.country')} *`"
        :options="countryOptions"
      />
    </div>
    <Button primary slide type="submit">
      {{ t('shop.addressCard.addressForm.save') }}
    </Button>
  </FormKit>

</FormKitLazyProvider>
</template>
<style scoped lang="scss">
.wrapper {
  margin-bottom: var(--form-spacing);

  display: grid;
  gap: var(--form-spacing);
  grid-template-columns: 1fr;
  @include breakpoint(ml) {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
